import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

// Utils
import { isEmpty, isNil, pick } from 'lodash';
import { PERMISSIONS } from '../../../constants/constants';
import { useHistory } from 'react-router-dom';
import { getInitialLocaleValues } from '../../../utils/locales';
import cn from '../../../utils/cn';
import { useFormik } from 'formik';

// Hooks
import useAppContext from '../../../hooks/useAppContext';
import usePermissionsState from '../../../hooks/usePermissionsState';
import useTestimoniesApi from '../../../hooks/Api/useTestimoniesApi';

// Components
import WithMenu from '../../../components/templates/WithMenu/WithMenu';

const permissions = [
  PERMISSIONS.PERM_TESTIMONIES_UPDATE,
  PERMISSIONS.PERM_TESTIMONIES_CREATE,
];

const TestimonyEdit = ({ match }) => {
  const history = useHistory();

  const { context: { locales, userInfo } } = useAppContext();
  const rolePermissions = usePermissionsState(userInfo, permissions);

  const [getTestimonyState, testimonyData,, { getTestimonyById }] = useTestimoniesApi();
  const [updateTestimonyState,, updateTestimonyError, { updateTestimony }] = useTestimoniesApi();
  const [createTestimonyState,, createTestimonyError, { createTestimony }] = useTestimoniesApi();

  const isEditing = !isEmpty(match?.params);

  const backToList = () => {
    history.push({
      pathname: "/testimonies",
      state: {},
    })
  }

  const isAllowedToEdit = rolePermissions[PERMISSIONS.PERM_TESTIMONIES_UPDATE];
  const isAllowedToCreate = rolePermissions[PERMISSIONS.PERM_TESTIMONIES_CREATE];
  const isAllowedToEditOrCreate = (isEditing && isAllowedToEdit) || (!isEditing && isAllowedToCreate);

  useEffect(() => {
    if (isEditing) {
      getTestimonyById(match.params.id).then((res) => {
        if (!res?.data) backToList();
      });
    }
  }, []);

  const createInitialValues = {
    title: locales ? getInitialLocaleValues(locales) : {},
    videoUrl: '',
  };

  const handleSubmit = (e, values) => {
    e.preventDefault();
    const testimony = pick(values, ['title', 'videoUrl']);

    if (isEditing) {
      // edit testimony
      updateTestimony({ testimonyId: testimonyData.id, testimony }).then((res) => {
        if (res?.status === 200) {
          history.push({
            pathname: "/testimonies",
            state: { submitType: "edit", success: true },
          });
        }
      });
    } else {
      // create new testimony
      createTestimony(testimony).then((res) => {
        if (res?.status === 201) {
          history.push({
            pathname: "/testimonies",
            state: { submitType: "create", success: true },
          });
        }
      });
    }
  };

  const formik = useFormik({
    initialValues: isEditing ? testimonyData || {} : createInitialValues,
    enableReinitialize: true,
  });
  
  return (
    <WithMenu>
      <div className="d-flex flex-row py-4 align-items-center justify-content-between">
        <h1>
          {`Témoignages / ${isEditing ? "Éditer" : "Ajouter"}`}
          {testimonyData?.title?.[1] ? ` / ${testimonyData?.title?.[1]}` : ''}
        </h1>
      </div>

      {(isEditing && !testimonyData) || getTestimonyState === 'loading' || isNil(locales) ? (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        />
      ) : (
        <form
          className="mw-100 w-75 m-auto" 
          onSubmit={(e) => handleSubmit(e, formik.values)}
        >
          <div className={cn(["card mb-4"])}>
            <div className="card-body">
              <div className="form-row">
                {Object.keys(locales).map(localeId => (
                  <div
                    key={`title_${localeId}`}
                    className="col-sm-6 col-md-6 pb-3"
                  >
                    <b>
                      <label htmlFor={`title.${localeId}`}>
                        {`Titre ${locales[localeId].code.toUpperCase()}`}
                      </label>
                    </b>
                    <input
                      required
                      type="text"
                      className="form-control"
                      id={`title.${localeId}`}
                      name={`title.${localeId}`}
                      value={formik.values?.title?.[localeId] || ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={!isAllowedToEditOrCreate}
                      placeholder={`Titre ${locales[localeId].code.toUpperCase()}`}
                    />
                    <div className="valid-tooltip">Looks good!</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={cn(["card mb-4"])}>
            <div className="card-body">
              <div className="form-row">
                <div
                  key="videoUrl"
                  className="col-sm-12 col-md-12 pb-3"
                >
                  <b>
                    <label htmlFor="videoUrl">
                      URL Vidéo (Youtube)
                    </label>
                  </b>
                  <input
                    required
                    type="text"
                    className="form-control"
                    id="videoUrl"
                    name="videoUrl"
                    value={formik.values?.videoUrl || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!isAllowedToEditOrCreate}
                    placeholder="URL Vidéo (Youtube)"
                  />
                  <div className="valid-tooltip">Looks good!</div>
                </div>
              </div>
            </div>
          </div>

          {updateTestimonyError?.data?.message || createTestimonyError?.data?.message ? (
            <div className="alert alert-danger" role="alert">
              {updateTestimonyError?.data?.message || createTestimonyError?.data?.message}
            </div>
          ) : null}

          <div className="d-flex align-items-center justify-content-end mb-4">
            <button
              onClick={backToList}
              type="button"
              className="btn btn-secondary mr-2"
            >
              Revenir à la liste
            </button>

            {isAllowedToEditOrCreate ? (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  updateTestimonyState === "loading" ||
                  createTestimonyState === "loading"
                }
              >
                {updateTestimonyState === "loading" || createTestimonyState === "loading" ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : null}
                {isEditing ? 'Enregistrer' : 'Ajouter'}
              </button>
            ) : null}
          </div>
        </form>
      )}
    </WithMenu>
  )
}

TestimonyEdit.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({}),
  }).isRequired,
};

export default TestimonyEdit;
